/**
 * AppearanceSettings — premium customization page.
 * 9 sections shipping in Phase 1: Mode, Primary color, Preset themes,
 * Corner radius, Density, Font, Animations, Accessibility (text/HC/motion),
 * Reset. Live-previews via AppearanceProvider; persisted per-clinic.
 */
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, Sun, Moon, Monitor, Check, RotateCcw, Sparkles,
  Type as TypeIcon, Gauge, CornerDownRight, Contrast, Zap,
  Sidebar as SidebarIcon, LineChart as LineChartIcon, Layers, Bell,
} from "lucide-react";
import { useAppearance } from "@/lib/AppearanceProvider";
import { apiError } from "@/lib/apiError";

const PRIMARY_SWATCHES = [
  { id: "saffron",  name: "Viyora Saffron", color: "#F5532D" },
  { id: "forest",   name: "Forest Green",  color: "#1A362B" },
  { id: "ocean",    name: "Ocean Blue",    color: "#1E5AA0" },
  { id: "purple",   name: "Royal Purple",  color: "#5E337A" },
  { id: "orange",   name: "Sunset Orange", color: "#D06A42" },
  { id: "ruby",     name: "Ruby Red",      color: "#B93A3A" },
  { id: "rose",     name: "Rose Pink",     color: "#D06090" },
  { id: "indigo",   name: "Indigo",        color: "#3F3F9A" },
  { id: "emerald",  name: "Emerald",       color: "#2D8F5A" },
  { id: "copper",   name: "Copper",        color: "#B27239" },
  { id: "slate",    name: "Slate",         color: "#3D4A57" },
];

const PRESET_THEMES = [
  { id: "saffron",  name: "Viyora Saffron",primary: "#F5532D", radius: 16, font: "Manrope",            swatch: ["#F5532D", "#FFF8F4"] },
  { id: "forest",   name: "Forest Green",  primary: "#1A362B", radius: 16, font: "Inter",              swatch: ["#1A362B", "#F6EFD8"] },
  { id: "minimal",  name: "Minimal White", primary: "#3D4A57", radius: 12, font: "Inter",              swatch: ["#3D4A57", "#FFFFFF"] },
  { id: "midnight", name: "Midnight Blue", primary: "#1E3A5F", radius: 16, font: "Geist",              swatch: ["#1E3A5F", "#E7ECF3"] },
  { id: "luxury",   name: "Luxury Black",  primary: "#1A1A1A", radius: 20, font: "Manrope",            swatch: ["#1A1A1A", "#F3EBD9"] },
  { id: "teal",     name: "Calm Teal",     primary: "#136A6E", radius: 20, font: "Inter",              swatch: ["#136A6E", "#E5F2F1"] },
  { id: "purple",   name: "Royal Purple",  primary: "#5E337A", radius: 16, font: "Plus Jakarta Sans",  swatch: ["#5E337A", "#F1EAF6"] },
  { id: "orange",   name: "Warm Orange",   primary: "#C4592A", radius: 16, font: "Manrope",            swatch: ["#C4592A", "#FDECE3"] },
  { id: "rose",     name: "Soft Rose",     primary: "#B24C6E", radius: 20, font: "Plus Jakarta Sans",  swatch: ["#B24C6E", "#F9EBF0"] },
];

const FONTS = ["Inter", "Geist", "SF Pro", "Manrope", "Plus Jakarta Sans"];
const RADII = [8, 12, 16, 20];
const DENSITY_OPTIONS = [
  { id: "compact",     label: "Compact",     hint: "Denser lists & tables" },
  { id: "comfortable", label: "Comfortable", hint: "The Viyora default" },
  { id: "spacious",    label: "Spacious",    hint: "Extra breathing room" },
];


export default function AppearanceSettings() {
  const { appearance: a, update, reset } = useAppearance();

  const set = async (patch) => {
    try { await update(patch); }
    catch (err) { toast.error(apiError(err, "Failed to save")); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="max-w-4xl mx-auto pb-16"
    >
      {/* Header + breadcrumb */}
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <nav className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink-soft mb-1">
            <Link to="/app/settings" className="hover:text-ink transition-colors inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Settings
            </Link>
            <span className="text-ink-disabled">/</span>
            <span className="text-ink-mid">Appearance</span>
          </nav>
          <h1 className="mt-2 font-display text-4xl tracking-tight text-ink">Appearance</h1>
          <p className="text-sm text-ink-mid mt-1">Personalize how Viyora looks for your clinic. Every change previews instantly.</p>
        </div>
        <button
          data-testid="btn-reset-appearance"
          onClick={async () => {
            if (!window.confirm("Reset all appearance settings to defaults?")) return;
            await reset();
            toast.success("Appearance reset");
          }}
          className="btn-outline text-sm inline-flex items-center gap-2"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Reset appearance
        </button>
      </div>

      <div className="space-y-8">
        {/* ===== Section 1 — Mode ===== */}
        <Section title="Appearance" caption="Light, dark, or follow your device.">
          <div className="grid grid-cols-3 gap-3" data-testid="sec-mode">
            {[
              { id: "light",  label: "Light",  icon: Sun,     tone: "bg-linen" },
              { id: "dark",   label: "Dark",   icon: Moon,    tone: "bg-[#17221E] text-[#E9E5D6]" },
              { id: "system", label: "System", icon: Monitor, tone: "bg-gradient-to-br from-linen to-[#17221E]/70" },
            ].map((m) => {
              const Icon = m.icon;
              const active = a.mode === m.id;
              return (
                <button
                  key={m.id} type="button"
                  data-testid={`mode-${m.id}`}
                  onClick={() => set({ mode: m.id })}
                  className={`relative rounded-2xl border p-3 text-left transition-all group ${
                    active ? "border-[var(--v-primary)] ring-2 ring-[var(--v-primary)]/20" : "border-[#EFEADC] hover:border-pine/25"
                  }`}
                >
                  <div className={`h-16 rounded-xl ${m.tone} grid place-items-center mb-2`}>
                    <Icon className={`w-5 h-5 ${m.id === "dark" ? "text-white/80" : "text-ink-mid"}`} />
                  </div>
                  <div className="text-sm font-medium text-ink flex items-center justify-between">
                    {m.label}
                    {active && <Check className="w-3.5 h-3.5 text-[var(--v-primary)]" />}
                  </div>
                </button>
              );
            })}
          </div>
        </Section>

        {/* ===== Section 2 — Primary color ===== */}
        <Section title="Primary color" caption="Applied to buttons, links, KPI accents, focus states.">
          <div className="flex items-center gap-3 flex-wrap" data-testid="sec-primary">
            {PRIMARY_SWATCHES.map((s) => {
              const active = a.primary.toLowerCase() === s.color.toLowerCase();
              return (
                <button
                  key={s.id} type="button"
                  data-testid={`swatch-${s.id}`}
                  onClick={() => set({ primary: s.color, preset: "custom" })}
                  title={s.name}
                  aria-label={s.name}
                  className={`w-9 h-9 rounded-full transition-all relative shadow-inner ${
                    active ? "ring-2 ring-offset-2 ring-offset-white ring-[var(--v-primary)] scale-110" : "hover:scale-110"
                  }`}
                  style={{ background: s.color }}
                >
                  {active && <Check className="w-3.5 h-3.5 text-white absolute inset-0 m-auto" />}
                </button>
              );
            })}
            <label className="ml-2 inline-flex items-center gap-2 text-xs text-ink-mid cursor-pointer">
              <input
                type="color" value={a.primary}
                onChange={(e) => set({ primary: e.target.value, preset: "custom" })}
                data-testid="custom-color-picker"
                className="w-9 h-9 rounded-full border-2 border-[#EFEADC] cursor-pointer bg-transparent"
              />
              Custom
            </label>
          </div>
        </Section>

        {/* ===== Section 3 — Preset themes ===== */}
        <Section title="Preset themes" caption="Curated bundles — color, radius, and font tuned together.">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sec-presets">
            {PRESET_THEMES.map((p) => {
              const active = a.preset === p.id;
              return (
                <button
                  key={p.id} type="button"
                  data-testid={`preset-${p.id}`}
                  onClick={() => set({ preset: p.id, primary: p.primary, radius: p.radius, font: p.font })}
                  className={`text-left rounded-2xl border p-3 transition-all group ${
                    active ? "border-[var(--v-primary)] ring-2 ring-[var(--v-primary)]/15" : "border-[#EFEADC] hover:border-pine/25 hover:-translate-y-0.5"
                  }`}
                >
                  <div className="h-14 rounded-xl overflow-hidden flex mb-2 shadow-inner">
                    <div className="flex-1" style={{ background: p.swatch[0] }} />
                    <div className="w-1/3" style={{ background: p.swatch[1] }} />
                  </div>
                  <div className="text-sm font-medium text-ink flex items-center justify-between">
                    <span className="truncate">{p.name}</span>
                    {active && <Check className="w-3.5 h-3.5 text-[var(--v-primary)] shrink-0" />}
                  </div>
                  <div className="text-[10px] text-ink-soft mt-0.5">{p.font}</div>
                </button>
              );
            })}
          </div>
        </Section>

        {/* ===== Section 5 — Corner radius ===== */}
        <Section title="Corner radius" caption="Applied to every card + gallery tile."
                 icon={CornerDownRight}>
          <div className="flex items-center gap-3" data-testid="sec-radius">
            {RADII.map((r) => (
              <button
                key={r} type="button"
                data-testid={`radius-${r}`}
                onClick={() => set({ radius: r })}
                className={`flex-1 py-4 text-sm border transition-all bg-white ${
                  a.radius === r ? "border-[var(--v-primary)] text-ink font-medium ring-2 ring-[var(--v-primary)]/15" : "border-[#EFEADC] text-ink-mid hover:border-pine/25"
                }`}
                style={{ borderRadius: `${r}px` }}
              >
                {r}px
              </button>
            ))}
          </div>
        </Section>

        {/* ===== Section 6 — Density ===== */}
        <Section title="Density" caption="Spacing across cards, tables, and forms." icon={Gauge}>
          <div className="grid grid-cols-3 gap-3" data-testid="sec-density">
            {DENSITY_OPTIONS.map((d) => {
              const active = a.density === d.id;
              return (
                <button
                  key={d.id} type="button"
                  data-testid={`density-${d.id}`}
                  onClick={() => set({ density: d.id })}
                  className={`rounded-2xl border p-4 text-left transition-all ${
                    active ? "border-[var(--v-primary)] ring-2 ring-[var(--v-primary)]/15 bg-white" : "border-[#EFEADC] hover:border-pine/25"
                  }`}
                >
                  <div className="text-sm font-medium text-ink flex items-center justify-between">
                    {d.label}
                    {active && <Check className="w-3.5 h-3.5 text-[var(--v-primary)]" />}
                  </div>
                  <div className="text-[11px] text-ink-soft mt-0.5">{d.hint}</div>
                </button>
              );
            })}
          </div>
        </Section>

        {/* ===== Section 7 — Font ===== */}
        <Section title="Font family" caption="Applied application-wide." icon={TypeIcon}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="sec-font">
            {FONTS.map((f) => (
              <button
                key={f} type="button"
                data-testid={`font-${f.replace(/\s+/g, '-')}`}
                onClick={() => set({ font: f })}
                className={`rounded-xl border py-3 px-3 text-sm transition-all ${
                  a.font === f ? "border-[var(--v-primary)] ring-2 ring-[var(--v-primary)]/15 bg-white" : "border-[#EFEADC] hover:border-pine/25"
                }`}
                style={{ fontFamily: `"${f}", ui-sans-serif, system-ui, sans-serif` }}
              >
                {f}
              </button>
            ))}
          </div>
        </Section>

        {/* ===== Section 9 — Animations ===== */}
        <Section title="Animations" caption="Hover lifts, staggered mounts, chart tweens." icon={Zap}>
          <Toggle
            testid="toggle-animations"
            checked={a.animations}
            onChange={(v) => set({ animations: v })}
            label="Enable premium animations"
            hint={a.animations ? "Motion is on across the app." : "Interface will feel static."}
          />
        </Section>

        {/* ===== Section 12 — Accessibility ===== */}
        <Section title="Accessibility" caption="Adjust text size, contrast, and motion." icon={Contrast}>
          <div className="space-y-4" data-testid="sec-a11y">
            {/* Text size */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Text size</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "small",  label: "Aa", note: "Small" },
                  { id: "medium", label: "Aa", note: "Medium" },
                  { id: "large",  label: "Aa", note: "Large" },
                ].map((t) => (
                  <button
                    key={t.id} type="button"
                    data-testid={`text-size-${t.id}`}
                    onClick={() => set({ text_size: t.id })}
                    className={`rounded-xl border py-3 text-center transition-all ${
                      a.text_size === t.id ? "border-[var(--v-primary)] ring-2 ring-[var(--v-primary)]/15 bg-white" : "border-[#EFEADC] hover:border-pine/25"
                    }`}
                  >
                    <div className={t.id === "small" ? "text-xs" : t.id === "medium" ? "text-base" : "text-lg"}>{t.label}</div>
                    <div className="text-[10px] text-ink-soft mt-1">{t.note}</div>
                  </button>
                ))}
              </div>
            </div>
            <Toggle testid="toggle-hc" checked={a.high_contrast} onChange={(v) => set({ high_contrast: v })} label="High-contrast mode" hint="Increases contrast for readability." />
            <Toggle testid="toggle-reduce-motion" checked={a.reduce_motion} onChange={(v) => set({ reduce_motion: v })} label="Reduce motion" hint="Disables all animations app-wide." />
          </div>
        </Section>

        {/* ===== Section 4 — Sidebar ===== */}
        <Section title="Sidebar style" caption="How the primary navigation feels." icon={SidebarIcon}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sec-sidebar">
            {[
              { id: "solid",    label: "Solid",    hint: "Default panel" },
              { id: "floating", label: "Floating", hint: "Detached w/ shadow" },
              { id: "compact",  label: "Compact",  hint: "Icons only" },
              { id: "expanded", label: "Expanded", hint: "Wider labels" },
              { id: "glass",    label: "Glass",    hint: "Blurred backdrop" },
            ].map((o) => {
              const active = a.sidebar === o.id;
              return (
                <button
                  key={o.id} type="button"
                  data-testid={`sidebar-${o.id}`}
                  onClick={() => set({ sidebar: o.id })}
                  className={`rounded-2xl border p-3 text-left transition-all ${
                    active ? "border-[var(--v-primary)] ring-2 ring-[var(--v-primary)]/15 bg-white" : "border-[#EFEADC] hover:border-pine/25"
                  }`}
                >
                  <div className="h-14 rounded-xl bg-linen relative overflow-hidden mb-2 grid grid-cols-4">
                    <div className={`col-span-1 h-full ${
                      o.id === "compact"  ? "bg-pine w-8"       :
                      o.id === "expanded" ? "bg-pine col-span-2":
                      o.id === "glass"    ? "bg-pine/70 backdrop-blur-sm" :
                      o.id === "floating" ? "bg-pine m-1 rounded-lg shadow" :
                                            "bg-pine"
                    }`} />
                  </div>
                  <div className="text-sm font-medium text-ink flex items-center justify-between">
                    {o.label}
                    {active && <Check className="w-3.5 h-3.5 text-[var(--v-primary)]" />}
                  </div>
                  <div className="text-[10px] text-ink-soft mt-0.5">{o.hint}</div>
                </button>
              );
            })}
          </div>
        </Section>

        {/* ===== Section 8 — Chart style ===== */}
        <Section title="Chart style" caption="Applied to dashboard revenue chart + sparklines." icon={LineChartIcon}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sec-chart">
            {[
              { id: "modern",   label: "Modern",   hint: "Balanced gradient" },
              { id: "classic",  label: "Classic",  hint: "Bold fill, thick" },
              { id: "minimal",  label: "Minimal",  hint: "Line only" },
              { id: "gradient", label: "Gradient", hint: "Vivid fill" },
            ].map((o) => {
              const active = a.chart === o.id;
              return (
                <button
                  key={o.id} type="button"
                  data-testid={`chart-${o.id}`}
                  onClick={() => set({ chart: o.id })}
                  className={`rounded-2xl border p-3 text-left transition-all ${
                    active ? "border-[var(--v-primary)] ring-2 ring-[var(--v-primary)]/15 bg-white" : "border-[#EFEADC] hover:border-pine/25"
                  }`}
                >
                  <svg viewBox="0 0 100 40" className="h-14 w-full mb-2">
                    <defs>
                      <linearGradient id={`sk-${o.id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--v-primary)" stopOpacity={o.id === "classic" || o.id === "gradient" ? 0.55 : (o.id === "minimal" ? 0 : 0.35)} />
                        <stop offset="100%" stopColor="var(--v-primary)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <path d="M0,32 L20,18 L40,24 L60,10 L80,16 L100,6 L100,40 L0,40 Z"
                          fill={o.id === "minimal" ? "transparent" : `url(#sk-${o.id})`} />
                    <path d="M0,32 L20,18 L40,24 L60,10 L80,16 L100,6"
                          stroke="var(--v-primary)" strokeWidth={o.id === "classic" ? 3 : 2}
                          fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    {o.id !== "minimal" && [
                      [0,32],[20,18],[40,24],[60,10],[80,16],[100,6]
                    ].map(([x,y],i) => (
                      <circle key={i} cx={x} cy={y} r={o.id === "classic" ? 2 : 1.6} fill="var(--v-primary)" />
                    ))}
                  </svg>
                  <div className="text-sm font-medium text-ink flex items-center justify-between">
                    {o.label}
                    {active && <Check className="w-3.5 h-3.5 text-[var(--v-primary)]" />}
                  </div>
                  <div className="text-[10px] text-ink-soft mt-0.5">{o.hint}</div>
                </button>
              );
            })}
          </div>
        </Section>

        {/* ===== Section 10 — Card style ===== */}
        <Section title="Dashboard card style" caption="Applied to every card and gallery tile." icon={Layers}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sec-card">
            {[
              { id: "flat",     label: "Flat",     hint: "No shadow" },
              { id: "elevated", label: "Elevated", hint: "Soft lift" },
              { id: "glass",    label: "Glass",    hint: "Blurred bg" },
              { id: "outline",  label: "Outline",  hint: "Border only" },
            ].map((o) => {
              const active = a.card === o.id;
              return (
                <button
                  key={o.id} type="button"
                  data-testid={`card-${o.id}`}
                  onClick={() => set({ card: o.id })}
                  className={`rounded-2xl border p-3 text-left transition-all ${
                    active ? "border-[var(--v-primary)] ring-2 ring-[var(--v-primary)]/15 bg-white" : "border-[#EFEADC] hover:border-pine/25"
                  }`}
                >
                  <div className={`h-14 rounded-xl mb-2 ${
                    o.id === "flat"     ? "bg-white border border-[#EFEADC]" :
                    o.id === "elevated" ? "bg-white border border-[#EFEADC] shadow-[0_8px_20px_-10px_rgba(26,54,43,0.2)]" :
                    o.id === "glass"    ? "bg-white/50 backdrop-blur-sm border border-white/60" :
                                          "bg-transparent border-2 border-[#D6C4B0]"
                  }`} />
                  <div className="text-sm font-medium text-ink flex items-center justify-between">
                    {o.label}
                    {active && <Check className="w-3.5 h-3.5 text-[var(--v-primary)]" />}
                  </div>
                  <div className="text-[10px] text-ink-soft mt-0.5">{o.hint}</div>
                </button>
              );
            })}
          </div>
        </Section>

        {/* ===== Section 11 — Notification style ===== */}
        <Section title="Notification style" caption="Toast appearance for success, errors and warnings." icon={Bell}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sec-toast">
            {[
              { id: "minimal",  label: "Minimal"  },
              { id: "rounded",  label: "Rounded"  },
              { id: "floating", label: "Floating" },
              { id: "modern",   label: "Modern"   },
            ].map((o) => {
              const active = a.toast === o.id;
              return (
                <button
                  key={o.id} type="button"
                  data-testid={`toast-${o.id}`}
                  onClick={() => { set({ toast: o.id }); toast.success(`${o.label} toast preview`); }}
                  className={`rounded-2xl border p-3 text-left transition-all ${
                    active ? "border-[var(--v-primary)] ring-2 ring-[var(--v-primary)]/15 bg-white" : "border-[#EFEADC] hover:border-pine/25"
                  }`}
                >
                  <div className={`h-14 mb-2 flex items-center px-3 text-[10px] text-ink-mid bg-white border border-[#EFEADC] ${
                    o.id === "minimal"  ? "rounded-lg shadow-sm" :
                    o.id === "rounded"  ? "rounded-2xl" :
                    o.id === "floating" ? "rounded-xl shadow-lg -translate-y-1" :
                                          "rounded-xl shadow-md"
                  }`}>
                    <Check className="w-3 h-3 text-[var(--v-primary)] mr-2" /> Saved
                  </div>
                  <div className="text-sm font-medium text-ink flex items-center justify-between">
                    {o.label}
                    {active && <Check className="w-3.5 h-3.5 text-[var(--v-primary)]" />}
                  </div>
                </button>
              );
            })}
          </div>
        </Section>

        {/* ===== Live preview ===== */}
        <Section title="Live preview" caption="See how your choices look in a mini clinic-style card.">
          <PreviewCard />
        </Section>
      </div>
    </motion.div>
  );
}


function Section({ title, caption, icon: Icon, children }) {
  return (
    <div className="card-widget bg-white border border-[#EFEADC] shadow-[0_1px_2px_rgba(26,54,43,0.03)]">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className="w-8 h-8 rounded-xl bg-linen text-ink grid place-items-center ring-1 ring-inset ring-pine/10 shadow-inner shrink-0">
              <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
            </div>
          )}
          <div>
            <div className="font-display text-lg text-ink">{title}</div>
            {caption && <div className="text-[11px] text-ink-mid mt-0.5">{caption}</div>}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}


function Toggle({ checked, onChange, label, hint, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group" data-testid={testid}>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
        className={`mt-0.5 relative w-10 h-6 rounded-full transition-colors shrink-0 ${
          checked ? "bg-[var(--v-primary)]" : "bg-[#DDD8CC]"
        }`}
      >
        <span
          className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
          style={{ transform: checked ? "translateX(16px)" : "translateX(0)" }}
        />
      </button>
      <div>
        <div className="text-sm font-medium text-ink">{label}</div>
        {hint && <div className="text-[11px] text-ink-mid mt-0.5">{hint}</div>}
      </div>
    </label>
  );
}


function PreviewCard() {
  return (
    <div className="rounded-2xl border border-[#EFEADC] p-5 bg-gradient-to-br from-white to-linen/30">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-ink-soft">Preview</div>
          <div className="font-display text-xl text-ink">Sample metric</div>
        </div>
        <span
          className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-medium text-white"
          style={{ background: "var(--v-primary)" }}
        >
          Live
        </span>
      </div>
      <div className="flex items-end justify-between">
        <div className="font-display text-4xl text-ink tabular-nums">₹1,534</div>
        <button
          type="button"
          className="text-xs px-4 py-2 rounded-full text-white font-medium inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
          style={{ background: "var(--v-primary)" }}
        >
          <Sparkles className="w-3.5 h-3.5" /> Primary action
        </button>
      </div>
      <div className="mt-4 h-2 bg-linen rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: "72%", background: "var(--v-primary)" }} />
      </div>
    </div>
  );
}
